<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

import CampaignsAPI from 'dashboard/api/campaigns'; // GK-HUB: R4 dry-run
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignMetricsRow from '../CampaignMetricsRow.vue';
import CampaignTable from '../CampaignTable.vue';
import ConfirmDeleteCampaignDialog from './CampaignPage/ConfirmDeleteCampaignDialog.vue';
import { useCampaignDashboard } from '../composables/useCampaignDashboard';
import { useCampaignWizard } from './CampaignPage/WhatsAppCampaign/composables/useCampaignWizard';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const {
  activeTab,
  statusFilter,
  searchQuery,
  selectedCampaign,
  isDetailOpen,
  isFetching,
  filteredCampaigns,
  setTab,
  setStatusFilter,
  selectCampaign,
  closeDetail,
  refresh,
} = useCampaignDashboard();

const confirmDeleteRef = ref(null);
const campaignToDelete = ref(null);

const tabs = computed(() => [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'sms', label: 'SMS' },
  { key: 'live_chat', label: 'Live Chat' },
]);

const handleCreate = () => {
  if (activeTab.value === 'whatsapp') {
    router.push({ name: 'campaigns_whatsapp_new' });
  }
};

const handleDelete = campaign => {
  campaignToDelete.value = campaign;
  closeDetail();
  nextTick(() => {
    confirmDeleteRef.value?.dialogRef?.open();
  });
};

const handleMetricFilter = statusKey => {
  setStatusFilter(statusKey);
};

const statusFilterLabels = {
  active: 'CAMPAIGN.DASHBOARD.METRICS.ACTIVE',
  scheduled: 'CAMPAIGN.DASHBOARD.METRICS.SCHEDULED',
  success: 'CAMPAIGN.DASHBOARD.METRICS.SUCCESS',
  error: 'CAMPAIGN.DASHBOARD.METRICS.ERRORS',
};
const statusFilterLabel = computed(() =>
  statusFilter.value
    ? t(statusFilterLabels[statusFilter.value] || statusFilter.value)
    : ''
);

// Refresh stats when campaigns change (after delete)
const allCampaigns = useMapGetter('campaigns/getAllCampaigns');
watch(
  () => allCampaigns.value?.length,
  () => store.dispatch('campaigns/fetchStats')
);

// Detail panel helpers
const detailSendResults = computed(
  () => selectedCampaign.value?.send_results || {}
);

const formatDetailDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(typeof dateStr === 'number' ? dateStr * 1000 : dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const handleCloseDetail = () => {
  closeDetail();
};

// ── Phase 3: Action buttons ──

const handleExportCsv = () => {
  const errors = detailSendResults.value?.errors;
  if (!errors || errors.length === 0) return;

  const header = 'Telefone,Erro,Codigo';
  const rows = errors.map(err => {
    const phone = (err.phone || '').replace(/,/g, '');
    const error = (err.error || '').replace(/,/g, ' ').replace(/"/g, "'");
    const code = err.code || '';
    return `${phone},"${error}",${code}`;
  });

  const csvContent = [header, ...rows].join('\n');
  const fileName = `campanha-${selectedCampaign.value?.id || 'erros'}-falhas.csv`;
  downloadCsvFile(fileName, csvContent);
};

const { prefillFromCampaign } = useCampaignWizard();

const handleDuplicate = () => {
  if (!selectedCampaign.value) return;
  const campaign = selectedCampaign.value;
  prefillFromCampaign(campaign);
  closeDetail();
  router.push({ name: 'campaigns_whatsapp_new' });
};

const isResending = ref(false);

const handleResend = async () => {
  if (!selectedCampaign.value) return;
  const errors = detailSendResults.value?.errors;
  if (!errors || errors.length === 0) return;

  isResending.value = true;
  try {
    const campaign = selectedCampaign.value;
    const failedContacts = errors.map(err => ({
      phone: err.phone,
      name: err.name || '',
    }));

    await store.dispatch('campaigns/create', {
      title: `${campaign.title} (Reenvio)`,
      message: campaign.message,
      template_params: campaign.template_params,
      inbox_id: campaign.inbox?.id || campaign.inbox_id,
      scheduled_at: new Date().toISOString(),
      audience_type: 'spreadsheet',
      audience: [],
      audience_contacts: failedContacts,
      team_id: campaign.team?.id || campaign.team_id,
      variable_mapping: campaign.variable_mapping,
      // dispatch_type not needed — only template mode supported
    });

    useAlert(t('CAMPAIGN.DASHBOARD.ACTIONS.RESEND_SUCCESS'));
    closeDetail();
    refresh();
  } catch (error) {
    useAlert(t('CAMPAIGN.DASHBOARD.ACTIONS.RESEND_ERROR'));
  } finally {
    isResending.value = false;
  }
};

const hasErrors = computed(
  () => (detailSendResults.value?.errors?.length || 0) > 0
);

// GK-HUB: R4 — Dry-run test send
const isDryRunning = ref(false);
const handleDryRun = async () => {
  if (!selectedCampaign.value) return;
  const testPhone = window.prompt(
    t('CAMPAIGN.WHATSAPP.WIZARD.DRY_RUN_PHONE'),
    ''
  );
  if (testPhone === null) return; // cancelled
  isDryRunning.value = true;
  try {
    const response = await CampaignsAPI.dryRun(
      selectedCampaign.value.id,
      testPhone || undefined
    );
    const result = response.data;
    if (result.success) {
      useAlert(
        t('CAMPAIGN.WHATSAPP.WIZARD.DRY_RUN_SUCCESS', {
          messageId: result.message_id,
        })
      );
    } else {
      useAlert(
        t('CAMPAIGN.WHATSAPP.WIZARD.DRY_RUN_FAILED', { error: result.error })
      );
    }
  } catch (error) {
    useAlert(
      t('CAMPAIGN.WHATSAPP.WIZARD.DRY_RUN_FAILED', {
        error: error?.response?.data?.error || error.message,
      })
    );
  } finally {
    isDryRunning.value = false;
  }
};
// GK-HUB: end dry-run

const handleKeydown = e => {
  if (e.key === 'Escape' && isDetailOpen.value) {
    handleCloseDetail();
  }
};
</script>

<template>
  <div class="flex flex-col h-full" @keydown="handleKeydown">
    <!-- ══ Header ══ (same pattern as IspPainel) -->
    <div class="px-6 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.DASHBOARD.HEADER') }}
          </h1>
          <p class="text-[13px] text-n-slate-10 mt-0.5">
            {{ t('CAMPAIGN.DASHBOARD.SUBTITLE') }}
          </p>
        </div>
        <!-- eslint-disable vue/no-static-inline-styles -->
        <button
          v-if="activeTab === 'whatsapp'"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          :style="{
            background:
              'var(--brand-gradient, linear-gradient(135deg, #1b6b93, #5dade2))',
          }"
          @click="handleCreate"
        >
          <!-- eslint-enable vue/no-static-inline-styles -->
          + {{ t('CAMPAIGN.DASHBOARD.NEW_CAMPAIGN') }}
        </button>
      </div>
    </div>

    <!-- ══ Metrics ══ -->
    <div class="px-6 pt-4">
      <CampaignMetricsRow @filter="handleMetricFilter" />
    </div>

    <!-- ══ Tabs + Search ══ (same pattern as IspPainel filter bar) -->
    <div class="flex items-center justify-between gap-3 flex-wrap px-6 pb-3">
      <!-- Tabs -->
      <div
        class="flex items-center bg-n-solid-2 rounded-lg p-0.5 border border-n-weak"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
          :class="
            activeTab === tab.key
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-9 hover:text-n-slate-11'
          "
          @click="setTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="flex items-center gap-2">
        <!-- Status filter pill -->
        <span
          v-if="statusFilter"
          class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-n-blue-3 text-n-blue-11 border border-n-blue-6"
        >
          {{ statusFilterLabel }}
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <button
            class="ml-0.5 hover:text-n-blue-12"
            @click="setStatusFilter(null)"
          >
            x
          </button>
          <!-- eslint-enable vue/no-bare-strings-in-template -->
        </span>

        <!-- Search -->
        <div class="relative">
          <span
            class="absolute i-lucide-search size-3.5 top-2 left-3 text-n-slate-9 z-10"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('CAMPAIGN.DASHBOARD.SEARCH_PLACEHOLDER')"
            class="reset-base w-56 h-8 pl-10 pr-3 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-transparent transition-colors"
          />
        </div>
      </div>
    </div>

    <!-- ══ Table ══ -->
    <div class="flex-1 overflow-auto px-6 pb-6">
      <div v-if="isFetching" class="flex items-center justify-center py-16">
        <Spinner />
      </div>
      <CampaignTable
        v-else
        :campaigns="filteredCampaigns"
        @select="selectCampaign"
        @delete="handleDelete"
      />
    </div>

    <!-- ══ Detail slide-over ══ -->
    <Teleport to="body">
      <Transition name="slide-over">
        <div
          v-if="isDetailOpen && selectedCampaign"
          class="fixed inset-0 z-50 flex justify-end"
        >
          <!-- Overlay -->
          <div
            class="absolute inset-0 bg-black/40"
            @click="handleCloseDetail"
          />
          <!-- Panel -->
          <div
            class="relative w-full max-w-4xl bg-n-solid-1 border-l border-n-weak shadow-xl overflow-y-auto"
          >
            <!-- Panel header -->
            <div
              class="sticky top-0 bg-n-solid-1 border-b border-n-weak px-6 py-4 z-10 flex items-center justify-between"
            >
              <div>
                <h2
                  class="text-lg font-semibold text-n-slate-12 truncate max-w-[380px]"
                >
                  {{ selectedCampaign.title }}
                </h2>
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border mt-1"
                  :class="{
                    'bg-n-teal-3 text-n-teal-11 border-n-teal-6': [
                      'success',
                      'active',
                    ].includes(selectedCampaign.computed_status),
                    'bg-n-amber-3 text-n-amber-11 border-n-amber-6': [
                      'scheduled',
                      'partial',
                      'retrying',
                    ].includes(selectedCampaign.computed_status),
                    'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6':
                      selectedCampaign.computed_status === 'failed',
                    'bg-n-solid-3 text-n-slate-9 border-n-weak':
                      selectedCampaign.computed_status === 'disabled',
                  }"
                >
                  {{
                    t(
                      'CAMPAIGN.DASHBOARD.STATUS.' +
                        (
                          selectedCampaign.computed_status || 'active'
                        ).toUpperCase()
                    )
                  }}
                </span>
              </div>
              <button
                class="p-2 rounded-lg hover:bg-n-solid-3 text-n-slate-9 hover:text-n-slate-12 transition-colors"
                @click="handleCloseDetail"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <!-- Panel body -->
            <div class="px-6 py-5 space-y-6">
              <!-- Send results -->
              <div v-if="selectedCampaign.campaign_type === 'one_off'">
                <h3
                  class="text-xs font-medium uppercase tracking-wider text-n-slate-9 mb-3"
                >
                  {{ t('CAMPAIGN.DASHBOARD.DETAIL.SEND_RESULT') }}
                </h3>
                <div class="grid grid-cols-3 gap-3">
                  <div
                    class="bg-n-solid-2 border border-n-weak rounded-lg p-3 text-center"
                  >
                    <div class="text-2xl font-bold text-n-slate-12">
                      {{ detailSendResults.total || 0 }}
                    </div>
                    <div class="text-[11px] text-n-slate-9 mt-0.5">
                      {{ t('CAMPAIGN.DASHBOARD.DETAIL.TOTAL_CONTACTS') }}
                    </div>
                  </div>
                  <div
                    class="bg-n-solid-2 border border-n-weak rounded-lg p-3 text-center"
                  >
                    <div class="text-2xl font-bold text-n-teal-11">
                      {{ detailSendResults.sent || 0 }}
                    </div>
                    <div class="text-[11px] text-n-slate-9 mt-0.5">
                      {{ t('CAMPAIGN.DASHBOARD.DETAIL.TOTAL_SENT') }}
                    </div>
                  </div>
                  <div
                    class="bg-n-solid-2 border border-n-weak rounded-lg p-3 text-center"
                  >
                    <div class="text-2xl font-bold text-n-ruby-11">
                      {{ detailSendResults.failed || 0 }}
                    </div>
                    <div class="text-[11px] text-n-slate-9 mt-0.5">
                      {{ t('CAMPAIGN.DASHBOARD.DETAIL.TOTAL_FAILED') }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Error summary -->
              <div
                v-if="
                  detailSendResults.error_summary &&
                  Object.keys(detailSendResults.error_summary).length > 0
                "
              >
                <h3
                  class="text-xs font-medium uppercase tracking-wider text-n-slate-9 mb-3"
                >
                  {{ t('CAMPAIGN.DASHBOARD.DETAIL.ERROR_SUMMARY') }}
                </h3>
                <div
                  class="bg-n-solid-2 border border-n-weak rounded-lg overflow-hidden"
                >
                  <table class="w-full text-sm">
                    <thead>
                      <tr class="border-b border-n-weak">
                        <th
                          class="text-left px-3 py-2 text-xs text-n-slate-9 font-medium"
                        >
                          {{ t('CAMPAIGN.DASHBOARD.DETAIL.CODE') }}
                        </th>
                        <th
                          class="text-right px-3 py-2 text-xs text-n-slate-9 font-medium"
                        >
                          {{ t('CAMPAIGN.DASHBOARD.DETAIL.COUNT') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(count, code) in detailSendResults.error_summary"
                        :key="code"
                        class="border-b border-n-weak last:border-0"
                      >
                        <td class="px-3 py-2">
                          <span
                            class="px-1.5 py-0.5 text-xs rounded bg-n-ruby-3 text-n-ruby-11 font-mono"
                          >
                            {{ code }}
                          </span>
                        </td>
                        <td
                          class="px-3 py-2 text-right text-n-slate-11 font-medium"
                        >
                          {{ count }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Error log -->
              <div
                v-if="
                  detailSendResults.errors &&
                  detailSendResults.errors.length > 0
                "
              >
                <h3
                  class="text-xs font-medium uppercase tracking-wider text-n-slate-9 mb-3"
                >
                  {{ t('CAMPAIGN.DASHBOARD.DETAIL.ERROR_LOG') }}
                </h3>
                <div
                  class="bg-n-solid-2 border border-n-weak rounded-lg max-h-[240px] overflow-y-auto"
                >
                  <table class="w-full text-xs">
                    <thead class="sticky top-0 bg-n-solid-2">
                      <tr class="border-b border-n-weak">
                        <th
                          class="text-left px-3 py-2 text-n-slate-9 font-medium"
                        >
                          {{ t('CAMPAIGN.DASHBOARD.DETAIL.PHONE') }}
                        </th>
                        <th
                          class="text-left px-3 py-2 text-n-slate-9 font-medium"
                        >
                          {{ t('CAMPAIGN.DASHBOARD.DETAIL.ERROR') }}
                        </th>
                        <th
                          class="text-left px-3 py-2 text-n-slate-9 font-medium"
                        >
                          {{ t('CAMPAIGN.DASHBOARD.DETAIL.CODE') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(err, idx) in detailSendResults.errors"
                        :key="idx"
                        class="border-b border-n-weak last:border-0"
                      >
                        <td
                          class="px-3 py-1.5 font-mono text-n-slate-11 whitespace-nowrap"
                        >
                          {{ err.phone }}
                        </td>
                        <td
                          class="px-3 py-1.5 text-n-ruby-11 truncate max-w-[200px]"
                        >
                          {{ err.error }}
                        </td>
                        <td class="px-3 py-1.5">
                          <span
                            class="px-1 py-0.5 rounded bg-n-solid-3 text-n-slate-10 font-mono"
                          >
                            {{ err.code }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Campaign info -->
              <div>
                <h3
                  class="text-xs font-medium uppercase tracking-wider text-n-slate-9 mb-3"
                >
                  {{ t('CAMPAIGN.DASHBOARD.DETAIL.CAMPAIGN_INFO') }}
                </h3>
                <div
                  class="bg-n-solid-2 border border-n-weak rounded-lg divide-y divide-n-weak"
                >
                  <div class="flex justify-between px-3 py-2.5">
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.CHANNEL')
                    }}</span>
                    <span class="text-xs text-n-slate-12 font-medium">{{
                      selectedCampaign.inbox?.name || '—'
                    }}</span>
                  </div>
                  <div
                    v-if="selectedCampaign.template_params?.name"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.TEMPLATE')
                    }}</span>
                    <span class="text-xs text-n-slate-12 font-medium">
                      {{
                        selectedCampaign.template_params.name
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, l => l.toUpperCase())
                      }}
                      <span class="text-n-slate-9 font-normal"
                        >({{
                          selectedCampaign.template_params.language || 'en'
                        }})</span
                      >
                    </span>
                  </div>
                  <div
                    v-if="selectedCampaign.team"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.TABLE.TEAM')
                    }}</span>
                    <span class="text-xs text-n-slate-12 font-medium">{{
                      selectedCampaign.team.name
                    }}</span>
                  </div>
                  <div class="flex justify-between px-3 py-2.5">
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.CREATED')
                    }}</span>
                    <span class="text-xs text-n-slate-12">{{
                      formatDetailDate(selectedCampaign.created_at)
                    }}</span>
                  </div>
                  <div
                    v-if="detailSendResults.started_at"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.STARTED')
                    }}</span>
                    <span class="text-xs text-n-slate-12">{{
                      formatDetailDate(detailSendResults.started_at)
                    }}</span>
                  </div>
                  <div
                    v-if="detailSendResults.finished_at"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.FINISHED')
                    }}</span>
                    <span class="text-xs text-n-slate-12">{{
                      formatDetailDate(detailSendResults.finished_at)
                    }}</span>
                  </div>
                  <div
                    v-if="detailSendResults.duration_seconds"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.DURATION')
                    }}</span>
                    <!-- eslint-disable vue/no-bare-strings-in-template --><!-- prettier-ignore -->
                    <span class="text-xs text-n-slate-12 font-medium">{{ detailSendResults.duration_seconds }}s</span>
                    <!-- eslint-enable vue/no-bare-strings-in-template -->
                  </div>
                  <!-- GK-HUB: R6 — Throttle + retry info -->
                  <div
                    v-if="selectedCampaign.throttle_interval_ms > 0"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.WHATSAPP.WIZARD.THROTTLE_LABEL')
                    }}</span>
                    <!-- eslint-disable vue/no-bare-strings-in-template --><!-- prettier-ignore -->
                    <span class="text-xs text-n-slate-12 font-medium">{{ selectedCampaign.throttle_interval_ms }}ms</span>
                    <!-- eslint-enable vue/no-bare-strings-in-template -->
                  </div>
                  <div
                    v-if="selectedCampaign.max_retries > 0"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.WHATSAPP.WIZARD.RETRY_LABEL')
                    }}</span>
                    <span class="text-xs text-n-slate-12 font-medium">
                      {{ selectedCampaign.retry_count ?? 0 }}/{{
                        selectedCampaign.max_retries
                      }}
                    </span>
                  </div>
                  <div
                    v-if="selectedCampaign.retry_after"
                    class="flex justify-between px-3 py-2.5"
                  >
                    <span class="text-xs text-n-slate-9">{{
                      t('CAMPAIGN.DASHBOARD.DETAIL.RETRY_NEXT')
                    }}</span>
                    <span class="text-xs text-n-amber-11 font-medium">{{
                      formatDetailDate(selectedCampaign.retry_after)
                    }}</span>
                  </div>
                  <!-- GK-HUB: end R6 retry info -->
                </div>
              </div>
            </div>

            <!-- Panel action footer -->
            <div
              v-if="selectedCampaign.campaign_type === 'one_off'"
              class="sticky bottom-0 bg-n-solid-1 border-t border-n-weak px-6 py-4 flex items-center gap-2 flex-wrap"
            >
              <button
                v-if="hasErrors"
                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12 transition-colors"
                @click="handleExportCsv"
              >
                <span class="i-lucide-download size-3.5" />
                {{ t('CAMPAIGN.DASHBOARD.ACTIONS.EXPORT_ERRORS') }}
              </button>
              <button
                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12 transition-colors"
                @click="handleDuplicate"
              >
                <span class="i-lucide-copy size-3.5" />
                {{ t('CAMPAIGN.DASHBOARD.ACTIONS.DUPLICATE') }}
              </button>
              <!-- GK-HUB: R4 — Dry-run test send button -->
              <button
                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12 transition-colors"
                :disabled="isDryRunning"
                @click="handleDryRun"
              >
                <span
                  v-if="isDryRunning"
                  class="i-lucide-loader-2 size-3.5 animate-spin"
                />
                <span v-else class="i-lucide-flask-conical size-3.5" />
                {{ t('CAMPAIGN.WHATSAPP.WIZARD.DRY_RUN') }}
              </button>
              <!-- GK-HUB: end dry-run button -->
              <!-- GK-HUB: R6 — Disable resend when auto-retry is scheduled -->
              <!-- eslint-disable vue/no-static-inline-styles -->
              <button
                v-if="hasErrors"
                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-white transition-colors disabled:opacity-50"
                :style="{
                  background:
                    'var(--brand-gradient, linear-gradient(135deg, #1b6b93, #5dade2))',
                }"
                :disabled="
                  isResending ||
                  (selectedCampaign.retry_after &&
                    selectedCampaign.retry_after * 1000 > Date.now())
                "
                @click="handleResend"
              >
                <span
                  v-if="isResending"
                  class="i-lucide-loader-2 size-3.5 animate-spin"
                />
                <span v-else class="i-lucide-send size-3.5" />
                {{
                  selectedCampaign.retry_after &&
                  selectedCampaign.retry_after * 1000 > Date.now()
                    ? t('CAMPAIGN.DASHBOARD.DETAIL.RETRY_SCHEDULED')
                    : t('CAMPAIGN.DASHBOARD.ACTIONS.RESEND')
                }}
              </button>
              <!-- eslint-enable vue/no-static-inline-styles -->
              <button
                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-n-ruby-6 text-n-ruby-11 hover:bg-n-ruby-3 transition-colors ml-auto"
                @click="handleDelete(selectedCampaign)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
                {{ t('CAMPAIGN.DASHBOARD.ACTIONS.DELETE') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete confirmation dialog -->
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteRef"
      :selected-campaign="campaignToDelete"
    />
  </div>
</template>

<style scoped>
.slide-over-enter-active,
.slide-over-leave-active {
  transition: all 0.3s ease;
}
.slide-over-enter-active > div:last-child,
.slide-over-leave-active > div:last-child {
  transition: transform 0.3s ease;
}
.slide-over-enter-from,
.slide-over-leave-to {
  opacity: 0;
}
.slide-over-enter-from > div:last-child,
.slide-over-leave-to > div:last-child {
  transform: translateX(100%);
}
</style>
